import { ReactElement, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import queryClient from 'libs/react-query';
import { i18nextInit } from 'libs/i18n';
import { axiosClientInit } from 'libs/axios';
import { embedAuthInit } from 'libs/embed-auth';
import publicFolderAxiosClient from 'libs/public-axios';
import { EmbedAuthProvider } from 'contexts/embed-auth-context';
import { SuspenseFallback } from 'components/suspense-fallback';
import { router } from './router';
import theme from './theme';
import { globalConfig, globalConfigUrl } from './configuration/config';

interface RuntimeConfig {
  suitabilityApiUrl?: string;
  wioApiUrl?: string;
  wioPdfUrl?: string;
  parentOrigin?: string;
  environment?: string;
  baseHref?: string;
}

const setEnvironment = ({
  suitabilityApiUrl,
  wioApiUrl,
  wioPdfUrl,
  parentOrigin,
  environment,
  baseHref,
}: RuntimeConfig) => {
  globalConfig.config = {
    suitabilityApiUrl:
      suitabilityApiUrl || process.env.SUITABILITY_API_URL || '',
    wioApiUrl: wioApiUrl || process.env.WIO_API_URL || '',
    wioPdfUrl: wioPdfUrl || process.env.WIO_PDF_URL || '',
    parentOrigin: parentOrigin || process.env.PARENT_ORIGIN || '',
    environment: environment || process.env.ENV_NAME || '',
    baseHref: baseHref || process.env.BASE_HREF || '',
  };
};

publicFolderAxiosClient
  .get<RuntimeConfig>(globalConfigUrl)
  .then((response) => {
    setEnvironment(response.data);

    i18nextInit();
    axiosClientInit();
    embedAuthInit();

    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles
            styles={{
              'html, body, #root': { height: '100%' },
            }}
          />
          <EmbedAuthProvider>
            <Suspense fallback={<SuspenseFallback />}>
              <RouterProvider
                router={router}
                future={{ v7_startTransition: true }}
              />
            </Suspense>
          </EmbedAuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  })
  .catch(() => <div>Error while fetching global config</div>)
  .then((app: ReactElement) => {
    ReactDOM.createRoot(document.getElementById('root')!).render(app);
  });
